import React, { useState, useEffect, useRef, memo } from 'react';

interface TimerWrapperProps {
  timeLimit: number;
  onTimeUp?: () => void;
  isActive?: boolean;
  className?: string;
  questionId: string; // ID unique de la question
}

const TimerWrapper: React.FC<TimerWrapperProps> = memo(({ 
  timeLimit, 
  onTimeUp, 
  isActive = true, 
  className = '',
  questionId
}) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const lastQuestionIdRef = useRef<string>(questionId);

  useEffect(() => {
    // Réinitialiser seulement si la question change
    if (lastQuestionIdRef.current !== questionId) {
      lastQuestionIdRef.current = questionId;
      setTimeLeft(timeLimit);
      setIsExpired(false);
      startTimeRef.current = Date.now();
    }

    if (!isActive) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, timeLimit - elapsed);
      
      setTimeLeft(remaining);

      if (remaining === 0) {
        setIsExpired(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (onTimeUp) {
          onTimeUp();
        }
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [questionId, isActive, onTimeUp, timeLimit]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimerColor = (): string => {
    if (isExpired) return '#dc3545'; // Rouge - temps écoulé
    if (timeLeft <= 30) return '#fd7e14'; // Orange - moins de 30s
    if (timeLeft <= 60) return '#ffc107'; // Jaune - moins de 1min
    return '#28a745'; // Vert - temps normal
  };

  const getTimerBackground = (): string => {
    if (isExpired) return '#f8d7da';
    if (timeLeft <= 30) return '#fff3cd';
    if (timeLeft <= 60) return '#fff3cd';
    return '#d4edda';
  };

  const getProgressPercentage = (): number => {
    return Math.max(0, Math.min(100, (timeLeft / timeLimit) * 100));
  };

  if (!isActive) {
    return null;
  }

  return (
    <div className={`timer-container ${className}`}>
      <div 
        className="timer-display"
        style={{
          background: getTimerBackground(),
          border: `2px solid ${getTimerColor()}`,
          color: getTimerColor(),
          padding: '8px 16px',
          borderRadius: '20px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 'bold',
          fontSize: '1.1em',
          transition: 'all 0.3s ease'
        }}
      >
        <span className="timer-icon">
          {isExpired ? '⏰' : timeLeft <= 30 ? '⚠️' : '⏱️'}
        </span>
        <span className="timer-text">
          {isExpired ? 'Temps écoulé!' : formatTime(timeLeft)}
        </span>
      </div>
      
      {/* Barre de progression */}
      <div 
        className="timer-progress"
        style={{
          width: '100%',
          height: '4px',
          background: '#e9ecef',
          borderRadius: '2px',
          marginTop: '8px',
          overflow: 'hidden'
        }}
      >
        <div
          className="timer-progress-bar"
          style={{
            width: `${getProgressPercentage()}%`,
            height: '100%',
            background: getTimerColor(),
            transition: 'width 1s linear, background-color 0.3s ease',
            borderRadius: '2px'
          }}
        />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Comparaison personnalisée pour éviter les re-renders
  return (
    prevProps.timeLimit === nextProps.timeLimit &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.questionId === nextProps.questionId &&
    prevProps.className === nextProps.className &&
    prevProps.onTimeUp === nextProps.onTimeUp
  );
});

TimerWrapper.displayName = 'TimerWrapper';

export default TimerWrapper;
